import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import Menu, { menuClasses } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ArrowLeftRight } from 'lucide-react';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import AvailabilityStatusIndicator from '~/components/Status/AvailabilityStatusIndicator';
import { Image3D } from '~/domain/patient/image3d';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import { cutUID } from '~/utils/string';

type ImagePickerProps = {
    currentPrimaryImage: Image3D;
    availableImages: AvailableImage[];
    onChange: (image: AvailableImage) => void;
};

export default function ImagePicker({
    currentPrimaryImage,
    availableImages,
    onChange,
}: ImagePickerProps) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    return (
        <Box>
            <Tooltip title={<FormattedMessage id="changeImage" />}>
                <IconButton
                    color="primary"
                    size="small"
                    aria-controls="image-picker-menu"
                    aria-haspopup="true"
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    sx={{ p: 1 }}
                >
                    <ArrowLeftRight size={12} />
                </IconButton>
            </Tooltip>
            <Menu
                id="image-picker-menu"
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                sx={{
                    width: 'calc(240px + 8px)',
                    ml: -0.5,
                    [`& .${menuClasses.paper}`]: {
                        width: '240px',
                        marginTop: '4px',
                    },
                }}
                MenuListProps={{ dense: true }}
            >
                {availableImages?.map((image) => {
                    const hasLabel = !!image.label;
                    const isLoaded = image.isLoaded;
                    const isActive =
                        currentPrimaryImage.seriesMetadata.seriesInstanceUID ===
                        image.seriesInstanceUID;

                    return (
                        <MenuItem
                            key={image.seriesInstanceUID}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: isActive
                                    ? 'rgba(0, 0, 0, 0.2)'
                                    : 'inherit',
                                pointerEvents: isActive ? 'none' : 'auto',
                            }}
                            onClick={() => {
                                onChange(image);
                                setAnchorEl(null);
                            }}
                        >
                            <Box display="flex" width="100%">
                                <Box mr={1} ml={-1} width={12}>
                                    <AvailabilityStatusIndicator
                                        isLoaded={isLoaded}
                                        isActive={isActive}
                                    />
                                </Box>
                                <Box width="100%" overflow="hidden">
                                    <Typography
                                        color="white"
                                        fontSize={hasLabel ? 14 : 13}
                                        fontStyle={
                                            hasLabel ? 'normal' : 'italic'
                                        }
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {hasLabel
                                            ? image.label
                                            : 'No series description'}
                                    </Typography>
                                    <Box display="flex" gap={0.5}>
                                        <Typography
                                            variant="body2"
                                            fontSize={12}
                                            color={'textSecondary'}
                                        >
                                            {image.modality}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontSize={12}
                                            color="textSecondary"
                                        >
                                            -{' '}
                                            {cutUID(
                                                image.seriesInstanceUID,
                                                4,
                                                4
                                            )}{' '}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </MenuItem>
                    );
                })}
            </Menu>
        </Box>
    );
}
